import { useCallback, useEffect, useMemo, useState } from "react";
import { getProducts, getProductBySlug } from "../services/productService";

function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function resolveImage(item) {
  return (
    item?.featured_image ||
    item?.image ||
    item?.cover_image ||
    item?.thumbnail ||
    item?.photo ||
    ""
  );
}

function normalizeProduct(item = {}) {
  const price = Number(item?.price ?? item?.harga ?? 0);
  const originalPrice = Number(item?.original_price ?? item?.harga_asli ?? 0);
  const stock = Number(item?.stock ?? item?.stok ?? 0);
  const soldCount = Number(item?.sold_count ?? item?.sold ?? 0);
  const rating = Number(item?.rating ?? 0);

  const categoryName =
    item?.category?.name ||
    item?.category_name ||
    item?.category ||
    "Lainnya";

  const categorySlug =
    item?.category?.slug ||
    item?.category_slug ||
    String(categoryName).toLowerCase().replace(/\s+/g, "-");

  const description =
    item?.description ||
    item?.short_description ||
    item?.excerpt ||
    "";

  return {
    ...item,
    id: item?.id,
    slug: item?.slug || "",
    name: item?.name || item?.title || "Produk",
    sku: item?.sku || "",
    short_description: item?.short_description || item?.excerpt || description,
    description,
    price,
    original_price: originalPrice > price ? originalPrice : originalPrice || null,
    stock: Number.isFinite(stock) ? stock : 0,
    sold_count: Number.isFinite(soldCount) ? soldCount : 0,
    rating: Number.isFinite(rating) ? rating : 0,
    image: resolveImage(item),
    featured_image: resolveImage(item),
    category_name: categoryName,
    category_slug: categorySlug,
    is_featured: Boolean(item?.is_featured),
    attributes: Array.isArray(item?.attributes) ? item.attributes : [],
    tags: item?.tags || "",
  };
}

export default function useProducts(initialParams = {}) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    category: "all",
    stock: "all",
    sort: "featured",
  });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getProducts(initialParams);
      const normalized = toArray(response).map(normalizeProduct);
      setProducts(normalized);

      const uniqueCategories = Array.from(
        new Map(
          normalized.map((item) => [
            item.category_slug,
            { slug: item.category_slug, name: item.category_name },
          ])
        ).values()
      );
      setCategories(uniqueCategories);
    } catch (err) {
      setError(err?.message || "Gagal memuat produk.");
      setProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(initialParams)]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const loadProductBySlug = useCallback(async (slug) => {
    if (!slug) return null;

    try {
      setDetailLoading(true);
      setError("");
      const response = await getProductBySlug(slug);
      const normalized = normalizeProduct(response);
      setSelectedProduct(normalized);
      return normalized;
    } catch (err) {
      setError(err?.message || "Gagal memuat detail produk.");
      setSelectedProduct(null);
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const filteredProducts = useMemo(() => {
    const keyword = filters.keyword.trim().toLowerCase();

    let items = [...products];

    if (keyword) {
      items = items.filter((item) => {
        const haystack = [
          item.name,
          item.short_description,
          item.description,
          item.sku,
          item.category_name,
          item.tags,
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(keyword);
      });
    }

    if (filters.category !== "all") {
      items = items.filter((item) => item.category_slug === filters.category);
    }

    if (filters.stock === "ready") {
      items = items.filter((item) => Number(item.stock) > 0);
    } else if (filters.stock === "empty") {
      items = items.filter((item) => Number(item.stock) <= 0);
    }

    items.sort((a, b) => {
      switch (filters.sort) {
        case "newest":
          return Number(b.id || 0) - Number(a.id || 0);
        case "price_asc":
          return Number(a.price || 0) - Number(b.price || 0);
        case "price_desc":
          return Number(b.price || 0) - Number(a.price || 0);
        case "rating":
          return Number(b.rating || 0) - Number(a.rating || 0);
        case "popular":
          return Number(b.sold_count || 0) - Number(a.sold_count || 0);
        case "name_asc":
          return String(a.name || "").localeCompare(String(b.name || ""), "id");
        case "name_desc":
          return String(b.name || "").localeCompare(String(a.name || ""), "id");
        default:
          return Number(b.is_featured) - Number(a.is_featured) || Number(b.id || 0) - Number(a.id || 0);
      }
    });

    return items;
  }, [products, filters]);

  const featuredProducts = useMemo(
    () => products.filter((item) => item.is_featured).slice(0, 8),
    [products]
  );

  return {
    products,
    filteredProducts,
    featuredProducts,
    selectedProduct,
    categories,
    filters,
    setFilters,
    setProducts,
    setSelectedProduct,
    loading,
    detailLoading,
    error,
    reload: loadProducts,
    loadProductBySlug,
  };
}
